import moment from 'moment';
import api from '@/common/api';
import { utcToLocalDate } from '@/common/calendarTime';

// Публичная (гостевая) сторона календаря записи — без авторизации, отдельно
// от админского src/store/calendar/calendar.js. См. audit/calendar-api-reference.md,
// раздел «Публичная сторона — флоу гостя».
//
// Ограниченные ссылки (/booking/:code): код ссылки (calendar_link_code) добавляется во все
// гостевые запросы, бэк сам сужает каталог/время/бронь до того, что разрешено ссылкой.
// Без кода — общий календарь, как раньше. Пустой код бэку не отправляем: явно переданный
// пустой/неизвестный код = 404, а не общий календарь.

// Счётчики актуальных запросов: ответ применяется, только если за время ожидания не ушёл
// более новый запрос того же вида (быстрая смена даты/фильтра или кода ссылки).
const requestIds = { availability: 0, month: 0 };

const linkParams = (state) => (state.linkCode ? { calendar_link_code: state.linkCode } : {});

const filterParams = ({ expert, serviceType, service }) => {
  const params = {};
  if (expert) params.expert = expert;
  if (serviceType) params.service_type = serviceType;
  if (service) params.service = service;
  return params;
};

const initialState = () => ({
  linkCode: null,
  link: {
    loading: false,
    data: null, // ответ GET /calendar/public-links/{code}/ — { code, kind, expert, service_type }
    unavailable: false, // 404: код неверный/отключён или основной объект недоступен
    error: false, // прочие ошибки (сеть, 5xx) — можно повторить
  },
  catalog: {
    loading: false,
    services: [], // BookableService[] от GET /calendar/services/ с кодом ссылки
  },

  loading: false,
  error: [],
  options: [], // AvailabilityOption[] — плоский список слотов от GET /calendar/availability/

  // Отметки "в этот день у кого-то есть свободное время" для месячной сетки календаря.
  // date у /calendar/availability/ теперь необязателен (обновлено на бэке 04.09.2026): без него
  // отдаются все текущие незавершённые и будущие интервалы одним запросом — раскладываем их по
  // датам сами и оставляем только то, что попадает в отображаемый месяц.
  monthAvailability: {
    loading: false,
    dates: [], // string[] в формате YYYY-MM-DD
  },

  booking: {
    loading: false,
    error: [],
    result: null, // ответ POST /calendar/appointments/ после успешной брони
  },

  cancellation: {
    loading: false,
    error: [],
    done: false,
    conflict: false, // 409 — бронь уже неактуальна
  },
});

export default {
  namespaced: true,
  state: initialState,
  actions: {
    // Смена кода (в т.ч. переход /booking/<code> ↔ /booking) сбрасывает всё, что было
    // загружено под предыдущий код, и делает устаревшими незавершённые запросы.
    setLinkCode({ commit, state }, code) {
      const value = code || null;
      if (state.linkCode === value) return;
      requestIds.availability += 1;
      requestIds.month += 1;
      commit('RESET_FOR_LINK', value);
    },

    async resolveLink({ commit, state }) {
      const code = state.linkCode;
      if (!code) return null;
      commit('SET_LINK', { loading: true, unavailable: false, error: false });
      try {
        const response = await api.get(`/calendar/public-links/${encodeURIComponent(code)}/`);
        if (state.linkCode !== code) return null;
        commit('SET_LINK', { data: response });
        return response;
      } catch (err) {
        if (state.linkCode !== code) return null;
        commit('SET_LINK', err.status === 404 ? { data: null, unavailable: true } : { error: true });
        return null;
      } finally {
        if (state.linkCode === code) commit('SET_LINK', { loading: false });
      }
    },

    async loadCatalog({ commit, state }) {
      const code = state.linkCode;
      if (!code) return [];
      commit('SET_CATALOG', { loading: true });
      try {
        const response = await api.get('/calendar/services/', { params: linkParams(state) });
        if (state.linkCode !== code) return [];
        const services = Array.isArray(response) ? response : [];
        commit('SET_CATALOG', { services });
        return services;
      } catch (err) {
        if (state.linkCode !== code) return [];
        commit('SET_CATALOG', { services: [] });
        commit('SET_LINK', err.status === 404 ? { data: null, unavailable: true } : { error: true });
        return [];
      } finally {
        if (state.linkCode === code) commit('SET_CATALOG', { loading: false });
      }
    },

    async searchAvailability({ commit, state }, { date, ...filters }) {
      requestIds.availability += 1;
      const requestId = requestIds.availability;
      commit('SET_LOADING', true);
      commit('SET_ERROR', []);
      try {
        const params = { date, ...filterParams(filters), ...linkParams(state) };
        const response = await api.get('/calendar/availability/', { params });
        if (requestId !== requestIds.availability) return;
        commit('SET_OPTIONS', Array.isArray(response) ? response : []);
      } catch (err) {
        if (requestId !== requestIds.availability) return;
        commit('SET_ERROR', Object.entries(err.data || {}));
        commit('SET_OPTIONS', []);
        throw err;
      } finally {
        if (requestId === requestIds.availability) commit('SET_LOADING', false);
      }
    },

    resetOptions({ commit }) {
      commit('SET_OPTIONS', []);
    },

    // Фильтры — те же, что у searchAvailability: точки на сетке должны совпадать с тем,
    // что гость увидит, кликнув по дню.
    async loadMonthAvailability({ commit, state }, { month, ...filters }) {
      requestIds.month += 1;
      const requestId = requestIds.month;
      commit('SET_MONTH_LOADING', true);
      try {
        const monthStart = moment(month, 'YYYY-MM').startOf('month');
        const monthEnd = moment(month, 'YYYY-MM').endOf('month');
        const params = { ...filterParams(filters), ...linkParams(state) };
        const response = await api.get('/calendar/availability/', { params });
        if (requestId !== requestIds.month) return;
        const dates = new Set();
        (Array.isArray(response) ? response : []).forEach((option) => {
          const date = utcToLocalDate(option.starts_at);
          if (moment(date, 'YYYY-MM-DD').isBetween(monthStart, monthEnd, 'day', '[]')) {
            dates.add(date);
          }
        });
        commit('SET_MONTH_AVAILABILITY', [...dates]);
      } catch {
        if (requestId === requestIds.month) commit('SET_MONTH_AVAILABILITY', []);
      } finally {
        if (requestId === requestIds.month) commit('SET_MONTH_LOADING', false);
      }
    },

    async createAppointment({ commit, state }, payload) {
      commit('SET_BOOKING_LOADING', true);
      commit('SET_BOOKING_ERROR', []);
      try {
        const response = await api.post('/calendar/appointments/', { ...payload, ...linkParams(state) });
        commit('SET_BOOKING_RESULT', response);
        return response;
      } catch (err) {
        commit('SET_BOOKING_ERROR', Object.entries(err.data || {}));
        throw err;
      } finally {
        commit('SET_BOOKING_LOADING', false);
      }
    },

    resetBooking({ commit }) {
      commit('SET_BOOKING_RESULT', null);
      commit('SET_BOOKING_ERROR', []);
    },

    async cancelAppointment({ commit }, token) {
      commit('SET_CANCEL_LOADING', true);
      commit('SET_CANCEL_ERROR', { value: [], conflict: false });
      try {
        await api.post('/calendar/appointments/cancel/', { token });
        commit('SET_CANCEL_DONE', true);
      } catch (err) {
        if (err.status === 409) {
          commit('SET_CANCEL_ERROR', { value: [], conflict: true });
        } else {
          commit('SET_CANCEL_ERROR', { value: Object.entries(err.data || {}), conflict: false });
        }
        throw err;
      } finally {
        commit('SET_CANCEL_LOADING', false);
      }
    },
  },
  mutations: {
    RESET_FOR_LINK(state, code) {
      Object.assign(state, initialState(), { linkCode: code });
    },
    SET_LINK(state, patch) {
      state.link = { ...state.link, ...patch };
    },
    SET_CATALOG(state, patch) {
      state.catalog = { ...state.catalog, ...patch };
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_ERROR(state, value) {
      state.error = value;
    },
    SET_OPTIONS(state, options) {
      state.options = options;
    },
    SET_MONTH_LOADING(state, value) {
      state.monthAvailability.loading = value;
    },
    SET_MONTH_AVAILABILITY(state, dates) {
      state.monthAvailability.dates = dates;
    },
    SET_BOOKING_LOADING(state, value) {
      state.booking.loading = value;
    },
    SET_BOOKING_ERROR(state, value) {
      state.booking.error = value;
    },
    SET_BOOKING_RESULT(state, result) {
      state.booking.result = result;
    },
    SET_CANCEL_LOADING(state, value) {
      state.cancellation.loading = value;
    },
    SET_CANCEL_ERROR(state, { value, conflict }) {
      state.cancellation.error = value;
      state.cancellation.conflict = conflict;
    },
    SET_CANCEL_DONE(state, value) {
      state.cancellation.done = value;
    },
  },
  getters: {
    linkCode: (state) => state.linkCode,
    link: (state) => state.link,
    catalogServices: (state) => state.catalog.services,
    catalogLoading: (state) => state.catalog.loading,
    // Уникальные эксперты каталога ссылки — для выбора эксперта в ссылке на тип услуги.
    catalogExperts: (state) => {
      const seen = {};
      return state.catalog.services.reduce((acc, service) => {
        if (service.expert && !seen[service.expert.id]) {
          seen[service.expert.id] = true;
          acc.push(service.expert);
        }
        return acc;
      }, []);
    },

    loading: (state) => state.loading,
    error: (state) => state.error,
    options: (state) => state.options,
    booking: (state) => state.booking,
    cancellation: (state) => state.cancellation,
    monthAvailabilityDates: (state) => state.monthAvailability.dates,
    monthAvailabilityLoading: (state) => state.monthAvailability.loading,

    // Группировка плоского списка AvailabilityOption по эксперту, а внутри — по типу услуги
    // (у одного эксперта на одну дату может быть открыто несколько разных услуг, и без
    // подписи "какая это услуга" список времени было не отличить одно от другого).
    optionsByExpert: (state) => {
      const groups = [];
      const indexByExpertId = {};
      state.options.forEach((option) => {
        const expertId = option.expert && option.expert.id;
        if (indexByExpertId[expertId] === undefined) {
          indexByExpertId[expertId] = groups.length;
          groups.push({ expert: option.expert, services: [], indexByServiceTypeId: {} });
        }
        const group = groups[indexByExpertId[expertId]];
        const serviceTypeId = option.service_type && option.service_type.id;
        if (group.indexByServiceTypeId[serviceTypeId] === undefined) {
          group.indexByServiceTypeId[serviceTypeId] = group.services.length;
          group.services.push({ serviceType: option.service_type, slots: [] });
        }
        group.services[group.indexByServiceTypeId[serviceTypeId]].slots.push(option);
      });
      return groups.map(({ expert, services }) => ({ expert, services }));
    },
  },
};
